// src/components/Ouvidoria/OuvidoriaAdminView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { collection, query, onSnapshot, orderBy, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import {
    FaUser, FaUserSecret, FaTag, FaClock, FaSpinner, FaInbox, FaChartPie, FaLightbulb,
    FaReply, FaLink, FaCheckCircle, FaQuestionCircle, FaUsers, FaMapMarkerAlt, FaCalendarAlt,
    FaBullseye, FaTools, FaCoins, FaExclamationTriangle, FaSync, FaClipboardList, FaCopy,
    FaLock, FaInfoCircle
} from 'react-icons/fa';
import { GiLightBulb, GiStrong } from "react-icons/gi";
import { GoThumbsdown } from "react-icons/go";
import { VscWarning } from "react-icons/vsc";
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

// --- CONFIGURAÇÃO DAS METODOLOGIAS ---
const methodologies = {
    '5W2H': { name: 'Análise 5W2H', description: 'Ideal para obter um plano de ação claro, respondendo o quê, por quê, quem, onde, quando, como e quanto custará.', icon: FaQuestionCircle },
    'SWOT': { name: 'Análise SWOT', description: 'Use para identificar Forças, Fraquezas, Oportunidades e Ameaças a partir do feedback.', icon: GiStrong },
    'PDCA': { name: 'Ciclo PDCA', description: 'Perfeito para criar um ciclo de melhoria contínua: Planejar, Fazer, Checar e Agir.', icon: FaSync }
};

// ====================================================================================
// === COMPONENTES INTERNOS ===========================================================
// ====================================================================================

const AnalysisCredits = ({ used, total, compact = false }) => {
    const progressPercentage = total > 0 ? (used / total) * 100 : 0;
    const getNextMonthName = () => {
        const now = new Date();
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return nextMonth.toLocaleString('pt-BR', { month: 'long' });
    };

    if (compact) {
        return (
            <div className="credits-container-compact">
                <div className="compact-header">
                    <div className="compact-title"><FaCoins /><span>Créditos IA</span></div>
                    <div className="compact-usage"><strong>{used}</strong> / {total}</div>
                </div>
                <div className="progress-bar-container compact"><div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div></div>
                <div className="compact-footer"><FaInfoCircle /><span>Renova em 1º de {getNextMonthName()}</span></div>
            </div>
        );
    }

    return (
        <div className="credits-container">
            <div className="credits-header"><FaLightbulb /><h5>Créditos de Análise IA</h5></div>
            <div className="credits-usage"><span className="usage-numbers">{used} / {total}</span><span className="usage-text">utilizados</span></div>
            <div className="progress-bar-container"><div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div></div>
            <div className="credits-footer"><FaInfoCircle /><span>Renova em 1º de {getNextMonthName()}</span></div>
        </div>
    );
};

const DashboardMetrics = ({ submissions }) => {
    const stats = useMemo(() => ({
        total: submissions.length,
        novo: submissions.filter(s => s.status === 'novo').length,
        em_analise: submissions.filter(s => s.status === 'em_analise').length,
        resolvido: submissions.filter(s => s.status === 'resolvido').length,
    }), [submissions]);

    const categoryData = useMemo(() => {
        const counts = submissions.reduce((acc, sub) => { acc[sub.category] = (acc[sub.category] || 0) + 1; return acc; }, {});
        return {
            labels: Object.keys(counts).map(c => c.charAt(0).toUpperCase() + c.slice(1)),
            datasets: [{ data: Object.values(counts), backgroundColor: ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6c757d'], hoverOffset: 4 }],
        };
    }, [submissions]);

    return (
        <div className="dashboard-metrics-container">
            <div className="metric-cards">
                <div className="metric-card"><h4>{stats.total}</h4><span>Total</span></div>
                <div className="metric-card status-novo"><h4>{stats.novo}</h4><span>Novas</span></div>
                <div className="metric-card status-em_analise"><h4>{stats.em_analise}</h4><span>Em Análise</span></div>
                <div className="metric-card status-resolvido"><h4>{stats.resolvido}</h4><span>Resolvidas</span></div>
            </div>
            <div className="metric-chart">
                <h5><FaChartPie /> Distribuição por Categoria</h5>
                {submissions.length > 0 ? <Pie data={categoryData} options={{ responsive: true, maintainAspectRatio: false }} /> : <p>Nenhum dado para exibir.</p>}
            </div>
        </div>
    );
};

const ReplyModal = ({ submission, onClose, userData }) => {
    if (!submission) return null;
    const [replyMessage, setReplyMessage] = useState('');
    const [isReplying, setIsReplying] = useState(false);
    const handleSendReply = async () => {
        if (replyMessage.trim().length < 5) { alert("A resposta deve ter pelo menos 5 caracteres."); return; }
        setIsReplying(true);
        const docRef = doc(db, 'Empresas', userData.empresa_id, 'ouvidoria', submission.id);
        try {
            await updateDoc(docRef, { replies: arrayUnion({ message: replyMessage, repliedBy: userData.name, repliedAt: new Date() }) });
            setReplyMessage(''); onClose();
        } catch (error) { console.error("Erro ao enviar resposta:", error); } finally { setIsReplying(false); }
    };
    const handleStatusChange = async (newStatus) => {
        const docRef = doc(db, 'Empresas', userData.empresa_id, 'ouvidoria', submission.id);
        try { await updateDoc(docRef, { status: newStatus }); } catch (error) { console.error("Erro ao atualizar status:", error); }
    };
    return (<div className="modal-overlay-ouvidoria" onClick={onClose}><div className="modal-content-ouvidoria" onClick={e => e.stopPropagation()}><button className="modal-close-button" onClick={onClose}>&times;</button><h3>Detalhes da Manifestação</h3><div className="modal-details"><p><strong>Protocolo:</strong> {submission.id}</p><p><strong>Status:</strong> <span className={`status-badge status-${submission.status}`}>{submission.status.replace('_', ' ')}</span></p><p><strong>Data:</strong> {submission.createdAt?.toDate ? new Date(submission.createdAt.toDate()).toLocaleString('pt-BR') : '...'}</p><p><strong>Categoria:</strong> {submission.category}</p><p><strong>Autor:</strong> {submission.isAnonymous ? 'Anônimo' : `${submission.userName} (${submission.userDepartment || ''})`}</p></div><div className="modal-message"><strong>Mensagem Original:</strong><p>{submission.message}</p></div><div className="modal-history"><strong>Histórico de Respostas:</strong>{submission.replies && submission.replies.length > 0 ? (submission.replies.map((reply, index) => (<div key={index} className="reply-item"><p>{reply.message}</p><small>Respondido por {reply.repliedBy} em {reply.repliedAt?.toDate ? new Date(reply.repliedAt.toDate()).toLocaleString('pt-BR') : ''}</small></div>))) : (<p>Nenhuma resposta enviada ainda.</p>)}</div><div className="modal-actions"><div className="status-changer-modal"><label>Alterar status:</label><select defaultValue={submission.status} onChange={(e) => handleStatusChange(e.target.value)}><option value="novo">Novo</option><option value="em_analise">Em Análise</option><option value="resolvido">Resolvido</option></select></div><div className="reply-form"><label><FaReply /> Responder:</label><textarea rows="4" placeholder="Escreva uma resposta..." value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} /><button onClick={handleSendReply} disabled={isReplying}>{isReplying ? 'Enviando...' : 'Enviar Resposta'}</button></div></div></div></div>);
};

const ReportMessage = ({ title, text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => { if (!text) return; navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    return (<div className="report-message-container"><label>{title}</label><div className="report-message-box"><p>{text || "Nenhuma sugestão gerada."}</p><button onClick={handleCopy} title="Copiar" disabled={!text}>{copied ? <FaCheckCircle /> : <FaCopy />}</button></div></div>);
};

// --- ALTERAÇÃO ---: O modal de metodologia agora recebe 'cachedAnalyses'
const AnalysisMethodologyModal = ({ onSelect, onClose, submissionCount, usageData, cachedAnalyses }) => {
    const hasCredits = usageData.used < usageData.total;

    return (
        <div className="modal-overlay-ouvidoria" onClick={onClose}>
            <div className="modal-content-ouvidoria" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose}>&times;</button>
                <div className="modal-header-centered">
                    <AnalysisCredits used={usageData.used} total={usageData.total} compact />
                    <div className="header-icon-wrapper"><FaLightbulb className="header-icon" /></div>
                    <h3>Escolha uma Metodologia de Análise</h3>
                    <p>Selecione como a IA deve analisar e estruturar os insights.</p>
                    {submissionCount > 0 && (<div className="analysis-scope-info">Analisando <strong>{submissionCount}</strong> manifestaç{submissionCount > 1 ? 'ões' : 'ão'}</div>)}
                </div>
                {/* --- ALTERAÇÃO ---: Mensagem de aviso aprimorada */}
                {!hasCredits && (
                    <p className="no-credits-warning">
                        Você atingiu seu limite de análises este mês. Você ainda pode visualizar relatórios gerados anteriormente (se houver).
                    </p>
                )}
                <div className="methodology-selector">
                    {Object.entries(methodologies).map(([key, { name, description, icon: Icon }]) => {
                        // --- ALTERAÇÃO ---: Nova lógica para verificar se o card é clicável
                        const isCached = !!cachedAnalyses[key];
                        const isClickable = hasCredits || isCached;
                        return (
                            <div
                                key={key}
                                className={`methodology-card ${!isClickable ? 'disabled' : ''}`}
                                onClick={() => isClickable && onSelect(key)}
                                title={!isClickable ? "Créditos esgotados e sem análise em cache para estes filtros." : (isCached && !hasCredits ? "Visualizar última análise gerada (do cache)" : name)}
                            >
                                <div className="methodology-card-icon"><Icon /></div>
                                <div className="methodology-card-text">
                                    <h4>{name}</h4>
                                    <p>{description}</p>
                                    {/* --- ALTERAÇÃO ---: Aviso visual para resultados em cache */}
                                    {isCached && !hasCredits && (
                                        <span style={{ fontSize: '0.8em', color: '#007bff', fontWeight: 'bold', marginTop: '5px', display: 'flex', alignItems: 'center' }}>
                                            <FaClock style={{ marginRight: '5px' }} /> Ver relatório salvo
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- ALTERAÇÃO ---: O modal de insights agora recebe 'hasCredits'
const AiInsightsModal = ({ analysisResult, isLoading, error, onReanalyze, onClose, hasCredits }) => {
    const { result, methodology } = analysisResult || {};
    const renderSection = (title, icon, content) => (<div className="insight-card"><div className="insight-card-header">{icon}<h4>{title}</h4></div><div className="insight-card-body">{Array.isArray(content) ? <ul>{content.map((item, i) => <li key={i}>{item}</li>)}</ul> : <p>{content}</p>}</div></div>);
    const renderContent = () => {
        if (isLoading) return <div className="ai-feedback-state"><FaSpinner className="spinner" /><p>Analisando manifestações...</p></div>;
        if (error && error.isLimitError) return (<div className="ai-feedback-state error"><FaLock /><h4>Limite de Análises Atingido</h4><p>{error.message}</p><button className="btn-primary" style={{ marginTop: '1.5rem' }} onClick={() => alert('Redirecionando para a página de planos...')}>Fazer Upgrade</button></div>);
        if (error) return <div className="ai-feedback-state error"><FaExclamationTriangle /><h4>Ocorreu um Erro</h4><p>{error.message}</p></div>;
        if (!result) return <div className="ai-feedback-state"><FaInbox /><p>Nenhuma análise gerada.</p></div>;
        let analysisContent;
        switch (methodology) {
            case 'SWOT': analysisContent = <>{renderSection('Forças', <GiStrong />, result.strengths)}{renderSection('Fraquezas', <GoThumbsdown />, result.weaknesses)}{renderSection('Oportunidades', <GiLightBulb />, result.opportunities)}{renderSection('Ameaças', <VscWarning />, result.threats)}</>; break;
            case 'PDCA': analysisContent = <>{renderSection('Plan (Planejar)', <FaClipboardList />, result.plan)}{renderSection('Do (Fazer)', <FaTools />, result.do)}{renderSection('Check (Checar)', <FaCheckCircle />, result.check)}{renderSection('Act (Agir)', <FaSync />, result.act)}</>; break;
            default: analysisContent = <>{renderSection('What (O que?)', <FaQuestionCircle />, result.what)}{renderSection('Why (Por quê?)', <FaBullseye />, result.why)}{renderSection('Who (Quem?)', <FaUsers />, result.who)}{renderSection('Where (Onde?)', <FaMapMarkerAlt />, result.where)}{renderSection('When (Quando?)', <FaCalendarAlt />, result.when)}{renderSection('How (Como?)', <FaTools />, result.how)}{renderSection('How Much (Quanto?)', <FaCoins />, result.howMuch)}</>;
        }
        return (<>{<div className="ai-insights-grid">{analysisContent}</div>}{result.howToReport && (<div className="report-section"><h3><FaClipboardList /> Comunicação Estratégica</h3><p>Use estas mensagens como ponto de partida.</p><ReportMessage title="Para Gestores" text={result.howToReport.toManager} /><ReportMessage title="Para a Equipe" text={result.howToReport.toTeam} /><ReportMessage title="Para a Empresa" text={result.howToReport.toCompany} /></div>)}</>);
    };
    return (<div className="modal-overlay-ouvidoria" onClick={onClose}><div className="modal-content-ouvidoria large" onClick={e => e.stopPropagation()}><button className="modal-close-button" onClick={onClose}>&times;</button><div className="modal-header-with-action"><h3>{error && error.isLimitError ? <FaLock /> : <FaLightbulb />} {isLoading ? "Gerando Análise..." : (error ? "Aviso" : `Análise ${methodologies[methodology]?.name || ''}`)}</h3>
        {/* --- ALTERAÇÃO ---: O botão de reanálise agora verifica se há créditos */}
        {!isLoading && result && (<button
            onClick={onReanalyze}
            className="btn-reanalyze"
            title={hasCredits ? "Gerar nova análise com os mesmos filtros" : "Créditos de análise esgotados"}
            disabled={!hasCredits}
        ><FaSync /> Analisar Novamente</button>)}
    </div><div className="ai-insights-container">{renderContent()}</div></div></div>);
};

// ====================================================================================
// === COMPONENTE PRINCIPAL ===========================================================
// ====================================================================================

const OuvidoriaAdminView = ({ userData }) => {
    const [submissions, setSubmissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('todos');
    const [filterCategory, setFilterCategory] = useState('todas');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    const [showMethodologyModal, setShowMethodologyModal] = useState(false);
    const [showAiModal, setShowAiModal] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [analysisError, setAnalysisError] = useState(null);
    const [usageData, setUsageData] = useState({ used: 0, total: 10, period: '' });
    // --- ALTERAÇÃO ---: Novo estado para rastrear análises em cache
    const [cachedAnalyses, setCachedAnalyses] = useState({});

    const publicLink = `${window.location.origin}/ouvidoria-publica?empresaId=${userData.empresa_id}`;
    const handleCopyLink = () => { navigator.clipboard.writeText(publicLink).then(() => { setIsLinkCopied(true); setTimeout(() => setIsLinkCopied(false), 2500); }); };

    useEffect(() => {
        setIsLoading(true);
        const q = query(collection(db, 'Empresas', userData.empresa_id, 'ouvidoria'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setSubmissions(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => { console.error("Erro ao buscar manifestações:", error); setIsLoading(false); });
        return () => unsubscribe();
    }, [userData.empresa_id]);

    useEffect(() => {
        if (!userData.empresa_id) return;
        const currentPeriodId = new Date().toISOString().slice(0, 7);
        const usageDocRef = doc(db, 'Empresas', userData.empresa_id, 'analysisUsage', currentPeriodId);
        const unsubscribe = onSnapshot(usageDocRef, (doc) => {
            if (doc.exists()) {
                const usage = doc.data();
                setUsageData({ total: 10, used: usage.count || 0, period: currentPeriodId });
            } else {
                setUsageData({ total: 10, used: 0, period: currentPeriodId });
            }
        }, (error) => console.error("Erro ao buscar dados de uso da subcoleção:", error));
        return () => unsubscribe();
    }, [userData.empresa_id]);

    // --- ALTERAÇÃO ---: Novo useEffect para verificar o cache quando os filtros mudam
    useEffect(() => {
        const checkCache = () => {
            const availableCache = {};
            for (const methodology of Object.keys(methodologies)) {
                const cacheKey = `ouvidoriaAnalysis_${userData.empresa_id}_${filterStatus}_${filterCategory}_${startDate}_${endDate}_${methodology}`;
                try {
                    const cachedData = localStorage.getItem(cacheKey);
                    if (cachedData) {
                        const { timestamp } = JSON.parse(cachedData);
                        const cacheAge = (new Date() - new Date(timestamp)) / (1000 * 60 * 60 * 24);
                        if (cacheAge < 30) {
                            availableCache[methodology] = true;
                        }
                    }
                } catch (error) {
                    console.error("Falha ao verificar cache:", error);
                }
            }
            setCachedAnalyses(availableCache);
        };
        checkCache();
    }, [filterStatus, filterCategory, startDate, endDate, userData.empresa_id]);

    const filteredSubmissions = useMemo(() => {
        return submissions.filter(sub => {
            const statusMatch = filterStatus === 'todos' || sub.status === filterStatus;
            const categoryMatch = filterCategory === 'todas' || sub.category === filterCategory;
            let dateMatch = true;
            if (sub.createdAt?.toDate) {
                const subDate = sub.createdAt.toDate();
                if (startDate) { const start = new Date(startDate); start.setHours(0, 0, 0, 0); dateMatch = dateMatch && subDate >= start; }
                if (endDate) { const end = new Date(endDate); end.setHours(23, 59, 59, 999); dateMatch = dateMatch && subDate <= end; }
            }
            return statusMatch && categoryMatch && dateMatch;
        });
    }, [submissions, filterStatus, filterCategory, startDate, endDate]);

    const handleRunAnalysis = async (methodology, ignoreCache = false) => {
        setIsAnalyzing(true);
        setAnalysisError(null);
        setShowAiModal(true);
        const cacheKey = `ouvidoriaAnalysis_${userData.empresa_id}_${filterStatus}_${filterCategory}_${startDate}_${endDate}_${methodology}`;
        if (!ignoreCache) {
            try {
                const cachedData = localStorage.getItem(cacheKey);
                if (cachedData) {
                    const { result, timestamp } = JSON.parse(cachedData);
                    const cacheAge = (new Date() - new Date(timestamp)) / (1000 * 60 * 60 * 24);
                    if (cacheAge < 30) { setAnalysisResult({ result, methodology }); setIsAnalyzing(false); return; }
                }
            } catch (error) { console.error("Falha ao ler cache:", error); }
        }

        try {
            const getOuvidoriaInsights = httpsCallable(functions, 'getOuvidoriaInsights');
            const submissionsData = filteredSubmissions.map(s => ({ category: s.category, message: s.message, createdAt: s.createdAt?.toDate ? s.createdAt.toDate().toISOString() : new Date().toISOString() }));
            const result = await getOuvidoriaInsights({ submissions: submissionsData, methodology });
            if (result.data.success) {
                setAnalysisResult({ result: result.data.analysis, methodology });
                const dataToCache = { result: result.data.analysis, timestamp: new Date().toISOString() };
                localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
            } else { throw new Error(result.data.error || "A análise falhou."); }
        } catch (err) {
            if (err.code === 'permission-denied') {
                setAnalysisError({ isLimitError: true, message: err.message });
            } else { setAnalysisError({ isLimitError: false, message: err.message || "Não foi possível gerar a análise." }); }
        } finally { setIsAnalyzing(false); }
    };

    const handleMethodologySelected = (methodology) => { setShowMethodologyModal(false); handleRunAnalysis(methodology); };

    useEffect(() => { setAnalysisResult(null); }, [filterStatus, filterCategory, startDate, endDate]);

    if (isLoading) return <div className="loading-ouvidoria"><FaSpinner className="spinner" /> Carregando...</div>;

    // --- ALTERAÇÃO ---: Variável para verificar se há créditos e passar para os modais
    const hasCredits = usageData.used < usageData.total;

    return (
        <>
            <div className="public-link-info"><FaLink className="public-link-icon" /><div className="public-link-text"><strong>Link Público de Ouvidoria</strong><p>Compartilhe este link para receber manifestações externas.</p><code>{publicLink}</code></div><button onClick={handleCopyLink} className="btn-copy-link">{isLinkCopied ? <><FaCheckCircle /> Copiado!</> : 'Copiar Link'}</button></div>
            <DashboardMetrics submissions={filteredSubmissions} />
            <div className="admin-view-header">
                <h4><FaInbox /> Caixa de Entrada</h4>
                <button className="btn-ai-insights" onClick={() => setShowMethodologyModal(true)} disabled={filteredSubmissions.length === 0} title={filteredSubmissions.length === 0 ? "Filtre alguns dados para analisar" : "Analisar os dados filtrados com IA"}>
                    <FaLightbulb /> Analisar com IA
                </button>
            </div>
            <div className="cadastro-filters-container">
                <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}><option value="todos">Status (Todos)</option><option value="novo">Novo</option><option value="em_analise">Em Análise</option><option value="resolvido">Resolvido</option></select>
                <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}><option value="todas">Categoria (Todas)</option><option value="sugestao">Sugestão</option><option value="elogio">Elogio</option><option value="duvida">Dúvida</option><option value="reclamacao">Reclamação</option><option value="outro">Outro</option></select>
                <div className="filter-item-wrapper"><label htmlFor="startDate">De:</label><input id="startDate" type="date" className="form-select date-filter" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
                <div className="filter-item-wrapper"><label htmlFor="endDate">Até:</label><input id="endDate" type="date" className="form-select date-filter" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
            </div>
            {filteredSubmissions.length > 0 ? (<div className="ouvidoria-admin-table-wrapper"><table className="ouvidoria-admin-table"><thead><tr><th>Status</th><th>Identificação</th><th>Categoria</th><th>Data</th><th>Ações</th></tr></thead><tbody>{filteredSubmissions.map(sub => (<tr key={sub.id} onClick={() => setSelectedSubmission(sub)}><td><span className={`status-badge status-${sub.status}`}>{sub.status.replace('_', ' ')}</span></td><td className="identification-cell">{sub.isAnonymous ? <FaUserSecret title="Anônimo" /> : <FaUser title="Identificado" />}{sub.isAnonymous ? 'Anônimo' : sub.userName}</td><td><span className="category-tag"><FaTag /> {sub.category}</span></td><td>{sub.createdAt?.toDate ? new Date(sub.createdAt.toDate()).toLocaleDateString('pt-BR') : '...'}</td><td><button className="btn-details">Ver Detalhes</button></td></tr>))}</tbody></table></div>) : (<div className="empty-state-ouvidoria"><FaInbox size={50} /><h3>Nenhuma manifestação encontrada</h3><p>Tente ajustar os filtros ou aguarde por novas mensagens.</p></div>)}
            
            {selectedSubmission && <ReplyModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} userData={userData} />}
            
            {/* --- ALTERAÇÃO ---: Passando as novas props para os modais */}
            {showMethodologyModal && <AnalysisMethodologyModal
                onClose={() => setShowMethodologyModal(false)}
                onSelect={handleMethodologySelected}
                submissionCount={filteredSubmissions.length}
                usageData={usageData}
                cachedAnalyses={cachedAnalyses}
            />}
            {showAiModal && <AiInsightsModal
                onClose={() => setShowAiModal(false)}
                isLoading={isAnalyzing}
                error={analysisError}
                analysisResult={analysisResult}
                onReanalyze={() => handleRunAnalysis(analysisResult.methodology, true)}
                hasCredits={hasCredits}
            />}
        </>
    );
};

export default OuvidoriaAdminView;